<template>
    <div ref="root" class="multi-select">
        <label v-if="label" class="multi-select__label">{{ label }}</label>

        <button
            ref="trigger"
            type="button"
            class="multi-select__trigger"
            :class="{ 'multi-select__trigger--open': isOpen, 'multi-select__trigger--placeholder': !modelValue.length }"
            :aria-expanded="isOpen"
            aria-haspopup="listbox"
            @click="toggle"
            @keydown.down.prevent="openAndFocus(0)"
            @keydown.up.prevent="openAndFocus(options.length)"
            @keydown.esc="close"
        >
            <span class="multi-select__value">{{ displayValue }}</span>
            <span class="multi-select__chevron" aria-hidden="true"></span>
        </button>

        <ul
            v-if="isOpen"
            ref="menu"
            class="multi-select__menu"
            role="listbox"
            aria-multiselectable="true"
            :aria-label="label || placeholder"
            @keydown.esc.prevent="closeAndFocusTrigger"
            @keydown.down.prevent="moveFocus(1)"
            @keydown.up.prevent="moveFocus(-1)"
            @keydown.home.prevent="focusOption(0)"
            @keydown.end.prevent="focusOption(options.length)"
        >
            <li
                class="multi-select__option multi-select__option--select-all"
                :class="{ 'multi-select__option--selected': allSelected }"
                role="option"
                :aria-selected="allSelected"
                tabindex="-1"
                @click="toggleAll"
                @keydown.enter.prevent="toggleAll"
                @keydown.space.prevent="toggleAll"
            >
                <span class="multi-select__checkbox" :class="{ 'multi-select__checkbox--checked': allSelected, 'multi-select__checkbox--partial': !allSelected && modelValue.length > 0 }" aria-hidden="true"></span>
                {{ selectAllLabel }}
            </li>
            <li
                v-for="option in options"
                :key="option"
                class="multi-select__option"
                :class="{ 'multi-select__option--selected': isSelected(option) }"
                role="option"
                :aria-selected="isSelected(option)"
                tabindex="-1"
                @click="toggleOption(option)"
                @keydown.enter.prevent="toggleOption(option)"
                @keydown.space.prevent="toggleOption(option)"
            >
                <span class="multi-select__checkbox" :class="{ 'multi-select__checkbox--checked': isSelected(option) }" aria-hidden="true"></span>
                {{ option }}
            </li>
        </ul>
    </div>
</template>

<script>
export default {
    name: "MultiSelect",

    props: {
        label: {
            type: String,
            default: "",
        },
        options: {
            type: Array,
            default: () => [],
            validator: (options) => options.every((option) => typeof option === "string"),
        },
        placeholder: {
            type: String,
            default: "Select options",
        },
        modelValue: {
            type: Array,
            default: () => [],
        },
        // Shown in the box once every option is selected.
        allLabel: {
            type: String,
            default: "All",
        },
        selectAllLabel: {
            type: String,
            default: "Select all",
        },
    },

    emits: ["update:modelValue"],

    data() {
        return {
            isOpen: false,
        };
    },

    computed: {
        allSelected() {
            return this.options.length > 0 && this.options.every((option) => this.modelValue.includes(option));
        },

        displayValue() {
            if (!this.modelValue.length) return this.placeholder;
            if (this.allSelected) return this.allLabel;
            if (this.modelValue.length === 1) return this.modelValue[0];
            return `${this.modelValue.length} selected`;
        },
    },

    mounted() {
        document.addEventListener("pointerdown", this.handleOutsideClick);
    },

    beforeUnmount() {
        document.removeEventListener("pointerdown", this.handleOutsideClick);
    },

    methods: {
        toggle() {
            this.isOpen = !this.isOpen;
        },

        close() {
            this.isOpen = false;
        },

        closeAndFocusTrigger() {
            this.close();
            this.$nextTick(() => this.$refs.trigger.focus());
        },

        isSelected(option) {
            return this.modelValue.includes(option);
        },

        // The selection is emitted in the order of the options, whatever order it was picked in.
        toggleOption(option) {
            const selected = new Set(this.modelValue);
            if (selected.has(option)) {
                selected.delete(option);
            } else {
                selected.add(option);
            }
            this.$emit("update:modelValue", this.options.filter((item) => selected.has(item)));
        },

        toggleAll() {
            this.$emit("update:modelValue", this.allSelected ? [] : [...this.options]);
        },

        handleOutsideClick(event) {
            if (!this.$refs.root?.contains(event.target)) {
                this.close();
            }
        },

        openAndFocus(index) {
            this.isOpen = true;
            this.$nextTick(() => this.focusOption(index));
        },

        focusOption(index) {
            const options = this.$refs.menu?.querySelectorAll('[role="option"]');
            options?.[Math.max(0, Math.min(index, options.length - 1))]?.focus();
        },

        moveFocus(direction) {
            const options = [...this.$refs.menu.querySelectorAll('[role="option"]')];
            const currentIndex = options.indexOf(document.activeElement);
            this.focusOption(currentIndex + direction);
        },
    },
};
</script>

<style scoped>
.multi-select {
    position: relative;
    width: 100%;
    max-width: 100%;
    color: #080808;
    font-family: Arial, Helvetica, sans-serif;
}

.multi-select__label {
    display: block;
    margin: 0 0 4px 2px;
    color: #5f5f5f;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
}

.multi-select__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 40px;
    padding: 0 12px 0 12px;
    border: 1px solid #a8a8a8;
    border-radius: 8px;
    background: #ffffff;
    color: #080808;
    cursor: pointer;
    font: inherit;
    font-size: 20px;
    line-height: 1;
    text-align: left;
}

.multi-select__value {
    overflow: hidden;
    margin-right: 8px;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.multi-select__trigger:focus,
.multi-select__trigger--open {
    outline: 2px solid #ff4e00;
    outline-offset: 1.5px;
}

.multi-select__trigger--placeholder {
    color: #989898;
}

.multi-select__chevron {
    flex-shrink: 0;
    width: 0;
    height: 0;
    border-top: 6px solid #858585;
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
}

.multi-select__menu {
    position: absolute;
    z-index: 10;
    top: calc(100% + 3px);
    left: 3px;
    width: calc(100% - 6px);
    max-height: 280px;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
    overflow-y: auto;
    border: 1px solid #777777;
    background: #ffffff;
    list-style: none;
}

.multi-select__option {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 14px;
    line-height: 14px;
    outline: none;
}

.multi-select__option--select-all {
    border-bottom: 1px solid #dedede;
    font-weight: 600;
}

.multi-select__option:hover,
.multi-select__option:focus-visible,
.multi-select__option--selected {
    background: #858585;
    color: #ffffff;
}

.multi-select__checkbox {
    position: relative;
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    margin-right: 8px;
    border: 1px solid currentColor;
    border-radius: 2px;
}

.multi-select__checkbox--checked::after {
    content: "";
    position: absolute;
    top: 0px;
    left: 3px;
    width: 4px;
    height: 7px;
    border: solid currentColor;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.multi-select__checkbox--partial::after {
    content: "";
    position: absolute;
    top: 4px;
    left: 2px;
    width: 6px;
    height: 2px;
    background: currentColor;
}
</style>
